import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { extractErrorMessage } from '@/utils/errors';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Send, ChevronRight, ChevronLeft, Info, AlertTriangle } from 'lucide-react';
import {
  listMyBeneficiaries, listRates, createOperation,
} from '@/services/remittanceApi';
import QuickRemittanceForm from '@/components/remesas/QuickRemittanceForm';

const CURRENCIES_TO = [
  { key: 'CUP', label: 'CUP (Peso cubano)' },
  { key: 'MLC', label: 'MLC' },
  { key: 'USD', label: 'USD Clásica' },
];

// Funding currencies: the gestor pays the remittance from one of their two
// operational wallets (UYU or USD). USDT is an internal CEO bridge currency
// and is NEVER exposed to gestores or clients. The server-side 2-hop math
// (funding → USDT → destination) is in routers/remittance_simulator.py.
const FUNDING_CCYS = [
  { key: 'UYU', label: 'UYU (Peso uruguayo)' },
  { key: 'USD', label: 'USD (Dólares)' },
];

const STEPS = [
  'Beneficiario',
  'Monto y conversión',
  'Confirmación',
];

const Stepper = ({ step }) => (
  <div className="flex items-center gap-2 mb-6" data-testid="enviar-stepper">
    {STEPS.map((s, i) => (
      <div key={s} className="flex items-center gap-2 flex-1">
        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${i <= step ? 'bg-amber-500 text-slate-900' : 'bg-slate-200 text-slate-500'}`}>{i + 1}</div>
        <p className={`text-xs ${i === step ? 'font-bold text-slate-900' : 'text-slate-500'}`}>{s}</p>
        {i < STEPS.length - 1 && <div className={`flex-1 h-px ${i < step ? 'bg-amber-400' : 'bg-slate-200'}`} />}
      </div>
    ))}
  </div>
);

const GestorEnviarRemesaPage = () => {
  const { getAuthHeaders, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const simId = searchParams.get('sim');
  const isAdmin = user?.role === 'admin';
  // Default to fast/simple mode — bypasses the wizard's gestor-only
  // beneficiary/fund preconditions that break for admin and for any
  // gestor without saved bank accounts.
  const [mode, setMode] = useState(simId ? 'wizard' : 'quick');
  const fundingLabel = (key) => key === 'USD' ? 'USD (Dólares)' : 'UYU (Peso uruguayo)';
  const [step, setStep] = useState(0);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [rates, setRates] = useState([]);

  const [form, setForm] = useState({
    beneficiary_id: '',
    funding_currency: 'UYU',
    amount: '',
    currency_to: 'CUP',
    notes: '',
  });
  const [fund, setFund] = useState(null);
  const [busy, setBusy] = useState(false);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    // Wait for AuthContext to hydrate the user object before deciding which
    // endpoints to call — otherwise the very first render fires the gestor
    // path even for admin and triggers a transient "Error al cargar datos".
    if (!user) return;
    let cancelled = false;
    let ratesTimer = null;
    const refreshRates = async () => {
      try {
        const r = await listRates(getAuthHeaders);
        if (!cancelled) setRates(r.rates || []);
      } catch {
        if (!cancelled) setRates([]);
      }
    };
    // Admin reuses this page from the CEO shell but does NOT have gestor
    // beneficiaries / fund — skip those calls and only load rates.
    if (isAdmin) {
      refreshRates();
      // Iter 64 fix: poll every 30s so the wizard preview reflects the latest
      // "Zona de Cambios" values (single source of truth = usdt_rates_config).
      ratesTimer = setInterval(refreshRates, 30_000);
      return () => {
        cancelled = true;
        if (ratesTimer) clearInterval(ratesTimer);
      };
    }
    (async () => {
      try {
        const [b, r, f] = await Promise.all([
          listMyBeneficiaries(getAuthHeaders),
          listRates(getAuthHeaders),
          axios.get(`${API}/gestor/me/remittance-fund`, { headers: getAuthHeaders() }),
        ]);
        if (cancelled) return;
        setBeneficiaries(b.items || []);
        setRates(r.rates || []);
        setFund(f.data);
        // Prefill from a simulator entry, when navigated as /remesas/enviar?sim=<id>
        if (simId) {
          try {
            const histReq = await axios.get(`${API}/gestor/me/simulations?limit=200`, { headers: getAuthHeaders() });
            const sim = (histReq.data.items || []).find((s) => s.id === simId);
            if (sim) {
              const amtPrefill = sim.direction === 'forward'
                ? sim.breakdown.input_amount
                : sim.breakdown.input_amount;  // both store the "yo pago" leg
              setForm((prev) => ({
                ...prev,
                funding_currency: sim.input.currency_from,
                amount: String(amtPrefill),
                currency_to: sim.input.currency_to,
              }));
              setStep(0);  // beneficiary still required
              toast.info('Simulación cargada. Elige el beneficiario para continuar.');
            }
          } catch (prefillErr) {
            // Prefilling from a simulation is a UX nicety; log and continue.
            console.warn('[remesa] simulation prefill failed:', prefillErr?.message);
          }
        }
      } catch (e) {
        toast.error('Error al cargar datos');
      }
    })();
    // Iter 64 fix: poll every 30s so the wizard preview reflects the latest
    // "Zona de Cambios" values (single source of truth = usdt_rates_config).
    ratesTimer = setInterval(refreshRates, 30_000);
    return () => {
      cancelled = true;
      if (ratesTimer) clearInterval(ratesTimer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [simId, isAdmin, user?.id]);

  const beneficiary = useMemo(() => beneficiaries.find((b) => b.id === form.beneficiary_id) || null, [beneficiaries, form.beneficiary_id]);

  // 2-hop bridge: <funding>→USDT→<destination>. USDT is internal CEO-only.
  // The gestor only sees the effective rate (input * leg1 * leg2) and the
  // 5% commission applied on the destination side.
  // Customer-facing rate: prefer DIRECT funding→dest rate (CEO/Proveedor
  // panel). Falls back to USDT bridge only when no direct rate exists.
  const directPair = `${form.funding_currency}_${form.currency_to}`;
  const direct = useMemo(() => rates.find((x) => x.pair === directPair), [rates, directPair]);
  const leg1Pair = `${form.funding_currency}_USDT`;
  const leg2Pair = `USDT_${form.currency_to}`;
  const leg1     = useMemo(() => rates.find((x) => x.pair === leg1Pair), [rates, leg1Pair]);
  const leg2     = useMemo(() => rates.find((x) => x.pair === leg2Pair), [rates, leg2Pair]);
  const bridgeRate = (leg1 && leg2) ? +(leg1.rate * leg2.rate).toFixed(6) : null;
  const rate     = direct ? +Number(direct.rate).toFixed(6) : bridgeRate;
  const rateUsedKey = direct
    ? `${form.funding_currency}→${form.currency_to} directa`
    : `${form.funding_currency}→USD→${form.currency_to}`;
  // No customer-facing fees when direct rate is set — Aircom margin is in USDT spread.
  const commissionPct = direct ? 0 : (leg2 ? (leg2.commission_pct ?? 5) : 5);
  const profitPct     = direct ? 0 : (leg2 ? (leg2.profit_pct ?? 0) : 0);

  const amt        = Number(form.amount) || 0;
  const grossDest  = rate ? amt * rate : 0;
  const recibe     = grossDest * (1 - (commissionPct + profitPct) / 100);
  const ganancia   = +(grossDest * profitPct / 100).toFixed(2);
  const comisionAircom = +(grossDest * commissionPct / 100).toFixed(2);

  const bucket = fund?.kpis?.[form.funding_currency] || { balance: 0, block: false, alert: false };
  const insufficient = bucket.balance < amt;
  const blocked = bucket.block;

  const canNext = () => {
    if (step === 0) return !!form.beneficiary_id;
    if (step === 1) {
      const minAmt = form.funding_currency === 'USD' ? 15 : 500;
      return amt >= minAmt && !!form.currency_to && rate != null && !insufficient && !blocked;
    }
    return true;
  };

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        type: 'remittance',
        amount: amt,
        currency_from: form.funding_currency,
        currency_to: form.currency_to,
        metadata: {
          beneficiary_id: beneficiary.id,
          beneficiary_name: beneficiary.full_name,
          beneficiary_doc: beneficiary.document_id || null,
          beneficiary_phone: beneficiary.phone || null,
          beneficiary_bank: beneficiary.bank_name || null,
          beneficiary_account: beneficiary.account_number || null,
          beneficiary_currency: beneficiary.account_currency || null,
          rate_pair: rateUsedKey,
          rate_locked: rate,
          recibe_aproximado: recibe,
          ganancia,
          deuda: 0,
          funding_mode: 'gestor_balance',
          funding_currency: form.funding_currency,
          funding_amount: amt,
          gestor_notes: form.notes,
        },
      };
      const op = await createOperation(getAuthHeaders, payload);
      toast.success('Remesa creada · ID ' + op.id.slice(0, 8) + '… · enviada al proveedor en Cuba y al CEO');
      navigate('/remesas/pendientes');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Error al crear la remesa'));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl mx-auto" data-testid="gestor-enviar-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Send className="w-6 h-6 text-amber-600" />Enviar Remesa</h1>
        <p className="text-xs text-slate-500 mt-1">
          {mode === 'quick'
            ? 'Modo rápido: 4 campos, sin beneficiario guardado. Va directo a Pendientes.'
            : 'Proceso guiado en 3 pasos. La operación se descuenta de tu saldo (o queda como deuda con el CEO) y se asigna automáticamente al proveedor de monedas en Cuba.'}
        </p>
      </div>

      <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1" data-testid="remesa-mode-tabs">
        <button
          type="button"
          onClick={() => setMode('quick')}
          className={`px-4 py-1.5 text-xs font-semibold rounded-md transition-all ${mode === 'quick' ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
          data-testid="mode-quick-btn"
        >Modo rápido</button>
        <button
          type="button"
          onClick={() => setMode('wizard')}
          className={`px-4 py-1.5 text-xs font-semibold rounded-md transition-all ${mode === 'wizard' ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
          data-testid="mode-wizard-btn"
        >Modo avanzado (con beneficiario)</button>
      </div>

      {mode === 'quick' ? (
        <Card className="border-0 shadow-sm p-6">
          <QuickRemittanceForm />
        </Card>
      ) : (
      <>
      <Stepper step={step} />

      <Card className="border-0 shadow-sm p-6">
        {step === 0 && (
          <div className="space-y-3" data-testid="enviar-step-bene">
            <p className="text-sm font-semibold">Elige el beneficiario</p>
            {beneficiaries.length === 0 ? (
              <div className="p-6 text-center bg-amber-50 rounded border border-amber-200 text-sm text-amber-800">
                Aún no tienes beneficiarios. <a href="/remesas/cuentas" className="underline font-semibold">Crea uno primero</a>.
              </div>
            ) : (
              <Select value={form.beneficiary_id} onValueChange={(v) => setForm({ ...form, beneficiary_id: v })}>
                <SelectTrigger className="h-12" data-testid="enviar-bene-select"><SelectValue placeholder="Selecciona un beneficiario" /></SelectTrigger>
                <SelectContent>
                  {beneficiaries.map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.full_name} · {b.bank_name || 'Sin banco'} · {b.account_currency || '—'}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {beneficiary && (
              <div className="text-xs text-slate-600 bg-slate-50 p-3 rounded border">
                <p><b>{beneficiary.full_name}</b> · {beneficiary.document_id || 's/doc'}</p>
                <p>Banco: {beneficiary.bank_name || '—'} · Cuenta: <span className="font-mono">{beneficiary.account_number || '—'}</span></p>
              </div>
            )}
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4" data-testid="enviar-step-amount">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wide text-slate-500">Pagar con</label>
                <Select value={form.funding_currency} onValueChange={(v) => setForm({ ...form, funding_currency: v, amount: '' })}>
                  <SelectTrigger className="h-14 text-lg" data-testid="enviar-funding-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{FUNDING_CCYS.map((c) => <SelectItem key={c.key} value={c.key}>{fundingLabel(c.key)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wide text-slate-500">Monto a enviar ({fundingLabel(form.funding_currency)})</label>
                <Input type="number" min={form.funding_currency === 'USD' ? '5' : '100'} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="text-2xl font-mono h-14" data-testid="enviar-amount" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wide text-slate-500">Moneda de destino</label>
                <Select value={form.currency_to} onValueChange={(v) => setForm({ ...form, currency_to: v })}>
                  <SelectTrigger className="h-14 text-lg" data-testid="enviar-currency-to"><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES_TO.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="p-3 bg-slate-50 rounded border text-xs flex items-center justify-between" data-testid="enviar-fund-status">
              <span className="text-slate-600">Saldo disponible {form.funding_currency}: <b className="text-slate-900 font-mono">{bucket.balance.toLocaleString('es-ES', { minimumFractionDigits: 2 })}</b></span>
              {blocked && <span className="inline-flex items-center gap-1 text-rose-600 font-semibold"><AlertTriangle className="w-3.5 h-3.5" /> Fondo agotado (100%)</span>}
              {bucket.alert && !blocked && <span className="inline-flex items-center gap-1 text-amber-600 font-semibold"><AlertTriangle className="w-3.5 h-3.5" /> Sobre 80% de uso</span>}
            </div>
            {insufficient && amt > 0 && !blocked && (
              <p className="text-xs text-rose-600">Monto supera tu saldo disponible. Declara un depósito desde el Centro Financiero.</p>
            )}
            <div className="grid grid-cols-4 gap-3 pt-2 border-t">
              <div className="p-3 bg-slate-50 rounded">
                <p className="text-[10px] uppercase text-slate-500">Tasa efectiva</p>
                <p className="text-sm font-mono">{rate != null ? `1 ${form.funding_currency} = ${rate} ${form.currency_to}` : '—'}</p>
              </div>
              <div className="p-3 bg-rose-50 rounded">
                <p className="text-[10px] uppercase text-rose-700">Comisión Aircom ({commissionPct}%)</p>
                <p className="text-sm font-mono text-rose-700">{comisionAircom.toLocaleString('es-ES', { minimumFractionDigits: 2 })} {form.currency_to}</p>
              </div>
              <div className="p-3 bg-amber-50 rounded">
                <p className="text-[10px] uppercase text-slate-500">Beneficiario recibe</p>
                <p className="text-lg font-mono font-bold text-amber-700">{recibe.toLocaleString('es-ES', { minimumFractionDigits: 2 })} {form.currency_to}</p>
              </div>
              <div className="p-3 bg-emerald-50 rounded">
                <p className="text-[10px] uppercase text-slate-500">Tu ganancia ({profitPct}%)</p>
                <p className="text-lg font-mono font-bold text-emerald-700">{ganancia} {form.currency_to}</p>
              </div>
            </div>
            <p className="text-[11px] text-slate-400 italic">Tasa congelada al momento de crear la operación. Cualquier variación posterior no afecta este envío.</p>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3" data-testid="enviar-step-confirm">
            <p className="text-sm font-semibold">Confirma los datos antes de crear la remesa</p>
            <div className="border rounded-lg overflow-hidden">
              {[
                ['Beneficiario', `${beneficiary?.full_name} · ${beneficiary?.bank_name || '—'} · ${beneficiary?.account_currency || '—'}`],
                ['Pago con', form.funding_currency],
                ['Monto', `${amt} ${form.funding_currency} → ${recibe.toLocaleString('es-ES', { minimumFractionDigits: 2 })} ${form.currency_to}`],
                ['Tasa congelada', rate != null ? `${rate}` : '—'],
                ['Comisión Aircom', `${comisionAircom} ${form.currency_to} (${commissionPct}%)`],
                ['Tu ganancia', `${ganancia} ${form.currency_to} (${profitPct}%)`],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between px-3 py-2 border-b last:border-0 bg-white">
                  <span className="text-xs text-slate-500">{k}</span>
                  <span className="text-sm text-slate-800 font-medium">{v}</span>
                </div>
              ))}
            </div>
            <div className="p-3 bg-blue-50 border-l-4 border-blue-400 rounded flex items-start gap-2">
              <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
              <div className="text-xs text-blue-800">
                <p className="font-semibold">¿Qué pasa al crear esta remesa?</p>
                <ul className="list-disc list-inside mt-1 space-y-0.5">
                  <li>El monto se descuenta de tu saldo o queda como deuda con el CEO.</li>
                  <li>La operación llega automáticamente al <b>proveedor de monedas en Cuba</b>.</li>
                  <li>El <b>CEO</b> también la ve en su panel para seguimiento financiero.</li>
                </ul>
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wide text-slate-500">Notas internas (opcional)</label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="enviar-notes" />
            </div>
          </div>
        )}
      </Card>

      <div className="flex items-center justify-between">
        <Button variant="outline" disabled={step === 0 || busy} onClick={() => setStep((s) => Math.max(0, s - 1))} data-testid="enviar-prev"><ChevronLeft className="w-4 h-4 mr-1" />Atrás</Button>
        {step < STEPS.length - 1 ? (
          <Button disabled={!canNext() || busy} onClick={() => setStep((s) => s + 1)} className="bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="enviar-next">Siguiente<ChevronRight className="w-4 h-4 ml-1" /></Button>
        ) : (
          <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="enviar-submit"><Send className="w-4 h-4 mr-1" />{busy ? 'Creando…' : 'Crear remesa'}</Button>
        )}
      </div>
      </>
      )}
    </div>
  );
};

export default GestorEnviarRemesaPage;
